import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { MagnifyingGlass, User, Phone, Receipt, X } from "@phosphor-icons/react";

export default function Customers() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/customers?q=${encodeURIComponent(q)}&limit=100`);
      setList(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCustomer = async (c) => {
    setSelected(c);
    try {
      const r = await api.get(`/customers/${encodeURIComponent(c.phone)}/sales`);
      setHistory(r.data);
    } catch (e) { setHistory([]); }
  };

  return (
    <Shell>
      <PageHeader title="Customers" subtitle="Auto-saved by phone at every sale. Lifetime spend, bills, last visit." testId="customers-header" />
      <div className="p-8 space-y-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 max-w-md">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by phone, name or GSTIN…" className="w-full h-11 pl-9 pr-3 border border-slate-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <button type="submit" className="h-11 px-5 bg-slate-900 text-white font-semibold rounded-md text-sm">Search</button>
        </form>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">GSTIN</th>
                <th className="text-right px-4 py-3 font-semibold">Bills</th>
                <th className="text-right px-4 py-3 font-semibold">Lifetime Spend</th>
                <th className="text-left px-4 py-3 font-semibold">Last Visit</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400">No customers yet. They auto-save when a sale is created with a phone number.</td></tr>}
              {list.map((c) => (
                <tr key={c.phone} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openCustomer(c)} data-testid={`cust-row-${c.phone}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{c.name || "(no name)"}</td>
                  <td className="px-4 py-3 font-mono-num">{c.phone}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{c.gstin || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{c.total_bills || 0}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-bold text-emerald-700">{inr(c.total_spent || 0)}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{fmtIST(c.last_visit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="cust-modal">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-lg flex items-center gap-2"><User size={18} className="text-emerald-600" />{selected.name || "(no name)"}</div>
                <div className="text-xs text-slate-500 font-mono-num mt-0.5"><Phone size={11} className="inline mr-1" />{selected.phone} · {inr(selected.total_spent || 0)} lifetime · {selected.total_bills || 0} bills</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2 inline-flex items-center gap-1"><Receipt size={11} />Purchase History</div>
              {history.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No purchases recorded.</div>}
              {history.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                      <th className="text-left px-2 py-2 font-semibold">Date</th>
                      <th className="text-left px-2 py-2 font-semibold">Invoice</th>
                      <th className="text-right px-2 py-2 font-semibold">Items</th>
                      <th className="text-right px-2 py-2 font-semibold">Total</th>
                      <th className="text-left px-2 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100">
                        <td className="px-2 py-2 font-mono-num text-slate-600">{(s.created_at || "").slice(0, 10)}</td>
                        <td className="px-2 py-2 font-mono-num font-semibold">{s.invoice_no}</td>
                        <td className="px-2 py-2 text-right font-mono-num">{s.items?.length || 0}</td>
                        <td className="px-2 py-2 text-right font-mono-num font-bold">{inr(s.grand_total)}</td>
                        <td className="px-2 py-2 text-[10px] uppercase tracking-widest font-semibold">{s.cancelled ? <span className="text-red-600">Cancelled</span> : <span className="text-emerald-700">{s.payment_method}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
